/**
 * AgentCore Runtime type definitions.
 *
 * Defines interfaces for agent invocation and responses
 * that match the Python AgentCore Runtime models.
 */

export interface InvokeAgentOptions {
  sessionId: string;
  actorId: string;
  agentType: "vpn";
  agentCoreUrl?: string;
  image?: string;
  displayWidth?: number;
  displayHeight?: number;
  executionResult?: {
    success: boolean;
    output?: string;
    error?: string;
  };
}

export interface AgentResponse {
  action: "EXECUTE_COMMAND" | "INFO" | "COMPUTER_USE";
  state:
    | "NOT_STARTED"
    | "IN_PROGRESS"
    | "PENDING_USER_ACTION"
    | "COMPLETED"
    | "FAILED"
    | "TIMEOUT"
    | "CANCELLED";
  message: string;
  reasoning?: string;
  command_execution?: {
    command: string;
    timeout?: number; // Max execution time in seconds
    interval?: number; // Polling interval in seconds
    max_executions?: number;
    success_criteria?: string;
    failure_criteria?: string;
  };
  computer_use?: {
    action: string;
    coordinate?: [number, number];
    text?: string;
    scroll_direction?: string;
    scroll_amount?: number;
    key?: string;
    duration?: number; // For wait action (in seconds)
  };
  include_screenshot: boolean;
  next_request: string;
  workflow_timeout: number;
  /**
   * Where the hire signs in during a PENDING_USER_ACTION turn: the browser under the SAML flow,
   * the Cisco pop-up under the legacy one. The agent's own `message` says the same thing, but it
   * is English only and the user-turn screen renders translated instructions instead — so the
   * flow has to arrive as data. Absent means the agent did not say, and the neutral wording wins.
   */
  auth_location?: "browser" | "client";
}

export type AgentCoreOperationResult =
  | { success: true; response: AgentResponse }
  | { success: false; error: string };

/**
 * Chatbot agent types — conversational onboarding assistant.
 */

export interface ChatbotContext {
  current_step: string;
  current_sub_step: string;
  platform: string;
  hiring_country: string;
  verification_status: string;
  active_errors: string[];
  resolution_session_id?: string;
  alias: string;
  language: string;
}

export interface ChatbotRequest {
  agent_type: "chatbot";
  message: string;
  session_id: string;
  actor_id: string;
  context?: ChatbotContext;
  user_jwt: string;
}

export interface ChatbotResponse {
  message: string;
  tools_used: string[];
  media: Array<{ type: string; url: string; alt: string }>;
  escalation_summary: string;
  escalation_initiated: boolean;
  suggested_topics: string[];
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  suggestedTopics?: string[];
  media?: Array<{ type: string; url: string; alt: string }>;
}

/**
 * The macOS grants the assistant needs before it can act on the hire's screen.
 *
 * Mirrors the desktop host's own union. The host cannot import this module, so a rename on either
 * side has to be made on both, and the permissions page pins these two literals in a test.
 */
export type CapabilityBlockReason =
  | "accessibility-not-granted"
  | "screen-recording-not-granted";

/**
 * Which surface a grant click put in front of the hire.
 *
 * `dialog` is macOS's own consent dialog, which the host raises instead of a pane on a first
 * screen-capture ask. It counts as a trip because the host believes macOS raised it; the host
 * cannot confirm that, so a device that asked under an older build spends one silent click before
 * the next one opens the pane. Only `none` means the click opened nothing. Mirrors the host's own
 * union, so a rename on either side has to be made on both.
 */
export type PermissionGrantSurface = "pane" | "dialog" | "none";

/**
 * Whether the assistant can run, and which grants are still outstanding.
 *
 * `missingGrants` is carried on both branches because the walkthrough reads only that: it never
 * consults `outcome`, so a grant list is needed even where the host already calls itself available.
 */
export type CapabilityStatus =
  | {
      outcome: "available";
      isAvailable: true;
      missingGrants: readonly CapabilityBlockReason[];
    }
  | {
      outcome: "blocked";
      isAvailable: false;
      reasonId: CapabilityBlockReason;
      reasonIds: readonly CapabilityBlockReason[];
      missingGrants: readonly CapabilityBlockReason[];
    };

/**
 * Why a hire says a grant will not switch on, as offered by the report dialog.
 *
 * Here rather than beside the dialog because the metrics helpers carry it too, and a helper reaching
 * into a component directory for a contract inverts the layering.
 */
export const REPORT_CAUSES = [
  "status-stale",
  "app-missing",
  "toggle-reverts",
  "other",
] as const;

export type ReportCause = (typeof REPORT_CAUSES)[number];

/**
 * Why a hire is off the macOS grant walkthrough.
 *
 * `reported` is a grant they told us will not turn on; `unexplained` is one the host named that the
 * page cannot walk them through. They read the same to the hire except in the copy, and only the
 * first is a refusal they filed.
 */
export type GiveUpReason = "reported" | "unexplained";
